import { postAction } from '../api/client';
import { currentSession } from '../auth/session';

/** VAPID public keys are handed out base64url — the Push API wants raw bytes. */
function urlBase64ToUint8Array(base64url: string): Uint8Array {
  const padding = '='.repeat((4 - (base64url.length % 4)) % 4);
  const base64 = (base64url + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

export function pushSupported(): boolean {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

export async function currentPushSubscription(): Promise<PushSubscription | null> {
  if (!pushSupported()) return null;
  const reg = await navigator.serviceWorker.ready;
  return reg.pushManager.getSubscription();
}

/** Ask for permission, subscribe this device, and register it with the backend. */
export async function enablePush(vapidPublicKey: string): Promise<void> {
  if (!pushSupported()) throw new Error('Push notifications are not supported on this device or browser.');
  const ses = currentSession();
  if (!ses) throw new Error('Not signed in.');

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') throw new Error('Notification permission was not granted.');

  const reg = await navigator.serviceWorker.ready;
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapidPublicKey) as BufferSource,
    });
  }
  const j = sub.toJSON();
  await postAction('registerPush', {
    token: ses.token,
    subscription: { endpoint: j.endpoint, keys: j.keys },
  });
}

/** Unsubscribe this device, both locally and from the backend's list. */
export async function disablePush(): Promise<void> {
  const sub = await currentPushSubscription();
  if (!sub) return;
  const ses = currentSession();
  try {
    if (ses) await postAction('unregisterPush', { token: ses.token, endpoint: sub.endpoint });
  } finally {
    await sub.unsubscribe();
  }
}
